import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';

@Component({
  selector: 'app-sensor-chart',
  templateUrl: './sensor-chart.component.html',
  styleUrls: ['./sensor-chart.component.scss']
})
export class SensorChartComponent implements OnInit, OnChanges {

  @Input() tipoSensoreId: number = 0;
  @Input() tipoSensore: string = 'default';
  @Input() datiSensore: Array<{ timestamp: string, value: number, cumulativeValue: number }> = [];

  sensorMetaById: { [typeId: number]: { key: string, title: string, unit: string } } = {
    1: { key: 'barometri', title: 'Pressioni atmosferiche rilevate (hPa)', unit: '(hPa)' },
    2: { key: 'ventoscalare', title: 'Metri al secondo rilevati (m/s)', unit: '(m/s)' },
    3: { key: 'ventoraffica', title: 'Metri al secondo rilevati (m/s)', unit: '(m/s)' },
    12: { key: 'idrometri', title: 'Dati rilevati rispetto allo "0" idrometrico', unit: '(cm)' },
    13: { key: 'igrometri', title: 'Percentuali di umidità rilevate (%)', unit: '(%)' },
    24: { key: 'pluviometri', title: 'Istogramma cumulata oraria', unit: '(mm)' },
    25: { key: 'radiometri', title: 'Irradiazioni rilevate (W/m²)', unit: '(W/m²)' },
    31: { key: 'termometri', title: 'Temperatura oraria rilevata', unit: '(°C)' }
  };

  chartOptions: any;
  loading: boolean = true;
  dateHeaders: string[] = [];
  timeSegments: string[] = ['Mattina', 'Pomeriggio', 'Sera'];

  constructor() { }

  ngOnInit(): void {
    this.updateChartOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.datiSensore || changes.tipoSensore) {
      this.updateChartOptions();
    }
  }

  updateChartOptions(): void {
  if (!this.datiSensore || this.datiSensore.length === 0) {
    this.loading = true;
    return;
  }

  this.loading = false;

  const timestamps = this.datiSensore.map(d => d.timestamp);

  const values = this.datiSensore.map(d => {
    if (this.tipoSensoreId === 12) {
      return d.value * 100;
    }
    return d.value;
  });

  // Costruisci array cumulata: per pluviometri verrà calcolato dai valori orari
  let cumulatedValues: (number | null)[] = [];
  if (this.sensorMeta.key !== 'pluviometri') {
    cumulatedValues = this.datiSensore.map(d => {
      if (this.tipoSensoreId === 12) {
        return d.cumulativeValue * 100;
      }
      return d.cumulativeValue;
    });
  }

  // ✅ Costruisci una sequenza oraria continua tra primo e ultimo timestamp
  this.createContinuousHourlyStructure(timestamps, values, this.sensorMeta.key !== 'pluviometri' ? (cumulatedValues as number[]) : undefined);

  // Per i pluviometri calcola la cumulata giornaliera dai valori orari
  if (this.sensorMeta.key === 'pluviometri') {
    cumulatedValues = this.computePluviometerDailyCumulative(timestamps, values);
  }

  this.dateHeaders = this.getDateHeaders(timestamps);
  // Calcola indice di taglio (ultima ora con valore orario)
  const lastHourlyIndex = this.getLastDataIndex(
    timestamps,
    values as (number | null)[],
    (cumulatedValues as unknown as (number | null)[])
  );
  const tooltipFormatter = this.getTooltipFormatter(lastHourlyIndex);

  this.chartOptions = this.getBaseChartOptions(
    this.getChartTitle(),
    this.getChartUnit(),
    timestamps,
    values,
    cumulatedValues as number[],
    tooltipFormatter,
    lastHourlyIndex
  );
}
  createContinuousHourlyStructure(timestamps: string[], values: number[], cumulatedValues?: number[]): void {
  if (timestamps.length === 0) return;

  const parseLocal = (ts: string): Date => {
    const [date, time] = ts.split(' ');
    const [y, m, d] = date.split('-').map(Number);
    const [hh, mm, ss] = time.split(':').map(Number);
    return new Date(y, m - 1, d, hh, mm || 0, ss || 0, 0);
  };

  const formatLocal = (dt: Date): string => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const y = dt.getFullYear();
    const m = pad(dt.getMonth() + 1);
    const d = pad(dt.getDate());
    const h = pad(dt.getHours());
    return `${y}-${m}-${d} ${h}:00:00`;
  };

  const existing = new Map<string, { v: number | null, c: number | null }>();
  for (let i = 0; i < timestamps.length; i++) {
    existing.set(timestamps[i], {
      v: values[i] as unknown as number | null,
      c: cumulatedValues ? (cumulatedValues[i] as unknown as number | null) : null
    });
  }

  const start = parseLocal(timestamps[0]);
  const last = parseLocal(timestamps[timestamps.length - 1]);
  // Estendi sempre fino alle 23:00 dell'ultimo giorno per completare il terzo giorno
  const end = new Date(last.getFullYear(), last.getMonth(), last.getDate(), 23, 0, 0, 0);

  const fullTimestamps: string[] = [];
  const fullValues: (number | null)[] = [];
  const fullCumulated: (number | null)[] = [];

  const cursor = new Date(start);
  cursor.setMinutes(0, 0, 0);

  while (cursor.getTime() <= end.getTime()) {
    const key = formatLocal(cursor);
    fullTimestamps.push(key);
    const found = existing.get(key);
    if (found) {
      fullValues.push(found.v);
      if (cumulatedValues) fullCumulated.push(found.c);
    } else {
      fullValues.push(null);
      if (cumulatedValues) fullCumulated.push(null);
    }

    // Inserisci uno "slot" di separazione a 24:00 tra i giorni (gap visivo senza dati)
    const next = new Date(cursor);
    next.setHours(cursor.getHours() + 1);
    const dayChanges = next.getDate() !== cursor.getDate();
    if (dayChanges) {
      if (this.sensorMeta.key === 'pluviometri') {
        const gapLabel = `${key.substring(0, 10)} 24:00:00`;
        fullTimestamps.push(gapLabel);
        // Ore 24:00: nessun valore orario (bar), cumulata uguale alle 23:00 dello stesso giorno
        fullValues.push(null);
        if (cumulatedValues) {
          const cumulativeAt23 = found ? found.c : null;
          fullCumulated.push(cumulativeAt23);
        }
        // Slot GAP tra 24:00 e 00:00 successiva
        const gapEmptyLabel = `${key.substring(0, 10)}|gap`;
        fullTimestamps.push(gapEmptyLabel);
        fullValues.push(null);
        if (cumulatedValues) fullCumulated.push(null);
      }
      // per gli altri sensori: nessun inserimento, sequenza continua
    }

    cursor.setHours(cursor.getHours() + 1);
  }

  // Sostituisci gli array originali
  timestamps.length = 0;
  values.length = 0;
  timestamps.push(...fullTimestamps);
  values.push(...fullValues);
  if (cumulatedValues) {
    cumulatedValues.length = 0;
    cumulatedValues.push(...fullCumulated);
  }
}


  getDateHeaders(timestamps: string[]): string[] {
    // Escludi gli slot GAP e prendi solo stringhe data valide YYYY-MM-DD
    const rawDates = timestamps
      .filter(t => typeof t === 'string' && !t.endsWith('|gap'))
      .map(t => t.split(' ')[0]);

    const uniqueDates = [...new Set(rawDates)];

    // Mantieni solo gli ultimi 3 giorni
    if (uniqueDates.length > 3) {
      uniqueDates.splice(0, uniqueDates.length - 3);
    }

    const toLocale = (dateStr: string): string => {
      const [y, m, d] = dateStr.split('-').map(Number);
      const dt = new Date(y, (m || 1) - 1, d || 1);
      return dt.toLocaleDateString('it-IT');
    };

    return uniqueDates.map(toLocale);
  }

  getTooltipFormatter(cutIndex?: number): any {
    return {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
      formatter: (params: any) => {
        if (!params || !params.length || !params[0].name) return 'N/A';

        // Evita tooltip sullo slot GAP artificiale
        const name = params[0].name as string;
        if (name && name.endsWith('|gap')) return '';

        // Non mostrare tooltip oltre il taglio delle serie
        const idx = typeof params[0].dataIndex === 'number' ? params[0].dataIndex : -1;
        if (typeof cutIndex === 'number' && idx > cutIndex) return '';

        const [date, time] = name.split(' ');
        const unit = this.sensorMeta.unit.replace(/[()]/g, '');

        let tooltipText = `📅 ${date}<br/>🕒 ${time}<br/>`;

        if (this.sensorMeta.key === 'pluviometri') {
          // Per i pluviometri: serie 'bar' = valori orari, serie 'line' = cumulata giornaliera
          const hourlySeries = params.find((p: any) => p.seriesType === 'bar');
          const cumulativeSeries = params.find((p: any) => p.seriesType === 'line');

          // Mostra sempre la label per l'istogramma
          if (hourlySeries) {
            const val = hourlySeries.value;
            if (val !== null && val !== undefined) {
              tooltipText += `🔹 ${hourlySeries.seriesName}: <b>${typeof val === 'number' ? val.toLocaleString('it-IT') + ' ' + unit : 'N/A'}</b><br/>`;
            } else {
              // Alle 24:00: mostra il totale giornaliero (ultimo dato dalla somma delle cumulate orarie)
              const dailyTotal = cumulativeSeries && cumulativeSeries.value !== null && cumulativeSeries.value !== undefined
                ? cumulativeSeries.value
                : null;
              if (dailyTotal !== null) {
                tooltipText += `🔹 ${hourlySeries.seriesName}: <b>${typeof dailyTotal === 'number' ? dailyTotal.toLocaleString('it-IT') + ' ' + unit : ''}</b><br/>`;
              } else {
                // Fallback: solo label
                tooltipText += `🔹 ${hourlySeries.seriesName}<br/>`;
              }
            }
          }

          // Mostra valore cumulato (line)
          if (cumulativeSeries && cumulativeSeries.value !== null && cumulativeSeries.value !== undefined) {
            const val = cumulativeSeries.value;
            tooltipText += `🔸 Cumulata giornaliera: <b>${typeof val === 'number' ? val.toLocaleString('it-IT') + ' ' + unit : 'N/A'}</b><br/>`;
          }

        } else {
          const dataPoint = params[0];
          const val = dataPoint?.value;
          tooltipText += `🔹 ${dataPoint.seriesName}: <b>${typeof val === 'number' ? val.toLocaleString('it-IT') + ' ' + unit : 'N/A'}</b>`;
        }

        return tooltipText;
      }
    };
  }


  getChartTitle(): string {
    return this.sensorMetaById[this.tipoSensoreId]?.title || 'Dati Sensore';
  }

  getChartUnit(): string {
    return this.sensorMetaById[this.tipoSensoreId]?.unit || '';
  }

  getDateDividers(timestamps: string[]): any[] {
    const dividers: any[] = [];

    if (this.sensorMeta.key === 'pluviometri') {
      // Divider posizionato esattamente sullo slot GAP artificiale
      for (let i = 0; i < timestamps.length; i++) {
        if (timestamps[i].endsWith('|gap')) {
          dividers.push({
            xAxis: timestamps[i],
            lineStyle: { type: 'solid', color: '#999' },
            label: { show: false }
          });
        }
      }
      return dividers;
    }

    // Altri sensori: divider al cambio giorno reale
    for (let i = 1; i < timestamps.length; i++) {
      const [prevDate] = timestamps[i - 1].split(' ');
      const [currentDate] = timestamps[i].split(' ');
      if (prevDate !== currentDate) {
        dividers.push({
          xAxis: timestamps[i],
          lineStyle: { type: 'solid', color: '#999' },
          label: { show: false }
        });
      }
    }

    return dividers;
  }
calculateContinuousCumulative(timestamps: string[], cumulatedValues: number[]): (number | null)[] {
  const result: (number | null)[] = [];

  for (let i = 0; i < timestamps.length; i++) {
    const timestamp = timestamps[i];
    const time = timestamp.split(' ')[1];
    const hour = time ? time.substring(0, 2) : '';
    const minute = time ? time.substring(3, 5) : '';
    const cumulativeValue = cumulatedValues[i];

    // Usa il valore cumulativo così com'è; la separazione tra giorni è gestita a livello serie
    if (cumulativeValue !== null && cumulativeValue !== undefined) {
      result.push(Math.round(cumulativeValue * 10) / 10);
    } else {
      result.push(null);
    }
  }

  return result;
}

// Calcola la cumulata giornaliera per i pluviometri dai valori orari.
// Regole:
// - La cumulata si resetta alle 00:00 di ogni giorno
// - I valori partono dalle 01:00 (01:00 rappresenta 00:00-01:00)
// - Allo slot artificiale 24:00:00 la cumulata include anche il valore delle 00:00 del giorno successivo
private computePluviometerDailyCumulative(
  timestamps: string[],
  hourlyValues: (number | null)[]
): (number | null)[] {
  const result: (number | null)[] = new Array(timestamps.length).fill(null);
  let runningSum = 0;

  for (let i = 0; i < timestamps.length; i++) {
    const ts = timestamps[i];
    if (typeof ts !== 'string') continue;

    // Gestione slot GAP
    if (ts.endsWith('|gap')) {
      result[i] = null;
      continue;
    }

    const parts = ts.split(' ');
    if (parts.length !== 2) {
      result[i] = null;
      continue;
    }
    const time = parts[1];
    const hour = parseInt(time.substring(0, 2), 10);

    // Slot 24:00: somma il valore delle 00:00 del giorno successivo (i+2: salta il GAP)
    if (time.startsWith('24:')) {
      let nextDay00Val = 0;
      const nextIndex = i + 2; // i+1 è lo slot GAP
      if (
        nextIndex < hourlyValues.length &&
        typeof hourlyValues[nextIndex] === 'number'
      ) {
        nextDay00Val = (hourlyValues[nextIndex] as number) || 0;
      }
      result[i] = Math.round((runningSum + nextDay00Val) * 10) / 10;
      // Il reset reale avverrà alle 00:00 del giorno successivo
      continue;
    }

    // Alle 00:00 resetta la cumulata
    if (hour === 0) {
      runningSum = 0;
      result[i] = 0;
      continue;
    }

    // Dalle 01:00 alle 23:00 accumula
    const val = hourlyValues[i];
    if (typeof val === 'number') {
      runningSum += val;
    }
    result[i] = Math.round(runningSum * 10) / 10;
  }

  return result;
}

  getBaseChartOptions(
  title: string,
  unit: string,
  timestamps: string[],
  values: number[],
  cumulatedValues: number[],
  tooltipFormatter: any,
  lastHourlyIndex: number
) {
  const series: any[] = [];

  if (this.sensorMeta.key === 'pluviometri') {
    // ✅ Istogramma delle precipitazioni orarie (barre blu)
    series.push({
      name: 'Istogramma cumulata oraria',
      data: values,
      type: 'bar',
      barWidth: '95%',
      barGap: '0%',
      barCategoryGap: '5%',
      itemStyle: { color: 'rgb(0, 135, 197)' },
      label: { show: false },
      yAxisIndex: 0,
      markLine: {
        symbol: 'none',
        data: this.getDateDividers(timestamps),
        label: { show: false },
        silent: true,
        lineStyle: {
          color: '#999',
          width: 1,
          type: 'solid'
        }
      }
    });

    // ✅ Linea cumulata giornaliera (linea viola) - singola serie per tutti i giorni
    let allDaily = this.calculateContinuousCumulative(timestamps, cumulatedValues);
    // Taglia la linea oltre l'ultimo dato orario disponibile
    if (lastHourlyIndex >= 0 && Array.isArray(allDaily)) {
      allDaily = allDaily.map((v, idx) => (idx <= lastHourlyIndex ? v : null));
    }
    
    series.push({
      name: 'precip. cumulata giornaliera',
      data: allDaily,
      type: 'line',
      symbol: 'circle',
      showSymbol: false,
      symbolSize: 8,
      itemStyle: { 
        color: 'white',
        borderColor: 'rgb(114, 63, 148)',
        borderWidth: 2
      },
      emphasis: {
        showSymbol: true,
        symbolSize: 15,
        itemStyle: {
          color: 'white',
          borderColor: 'rgb(114, 63, 148)',
          borderWidth: 2,
          scale: 2.0
        }
      },
      lineStyle: {
        color: 'rgb(114, 63, 148)',
        width: 2,
        type: 'solid'
      },
      label: { show: false },
      connectNulls: false,
      yAxisIndex: 1
    });

  } else {
    series.push({
      name: title,
      data: values,
      type: 'line',
      barGap: '0%',
      barCategoryGap: '0%',
      smooth: false,
      animation: true,
      symbol: 'circle',
      showSymbol: false, // ✅ Nasconde i simboli di default
      symbolSize: 8, 
      emphasis: {
        // ✅ Mostra simboli solo in hover con dimensioni più grandi
        showSymbol: true,
        symbolSize: 15, // ✅ Pallino 3 volte più grande (5 * 3 = 15)
        itemStyle: {
          color: 'white',
          borderColor: '#29abe2',
          borderWidth: 2, // ✅ Bordo più spesso in hover
          scale: 2.0 // ✅ Nessuna scalatura aggiuntiva
        }
      },// ✅ Pallini invisibili di default
      lineStyle: {
        width: 3,
        color: '#29abe2'
      },
      markLine: {
        symbol: 'none',
        data: this.getDateDividers(timestamps),
        label: { show: false },
        silent: true,
        lineStyle: {
          color: '#eee',
          width: 0.5,
          type: 'solid'
        }
      }
    });
  }

  // ✅ Configurazione asse Y
  let yAxisConfig: any = {
    type: 'value',
    name: unit,
    nameLocation: 'end',
    nameGap: 10,
    axisLabel: {
      color: '#333',
      fontSize: 12,
      formatter: (value: number) => `${value.toLocaleString('it-IT')}`
    },
    axisPointer: {
      label: {
        formatter: (params: any) => {
          const value = params?.value;
          const formatted = typeof value === 'number'
            ? value.toLocaleString('it-IT')
            : 'N/A';
          return `${formatted} ${unit.replace(/[()]/g, '')}`;
        }
      }
    }
  };

  // ✅ CONFIGURAZIONE PLUVIOMETRI - VERSIONE SEMPLIFICATA E DINAMICA
  if (this.sensorMeta.key === 'pluviometri') {
    // Calcola i valori massimi reali
    const dailyCumulative = this.calculateContinuousCumulative(timestamps, cumulatedValues);
    const validCumulativeValues = dailyCumulative.filter(v => v !== null && v !== undefined && typeof v === 'number') as number[];
    const validHourlyValues = values.filter(v => v !== null && v !== undefined && typeof v === 'number') as number[];

    const maxCumulative = validCumulativeValues.length > 0 ? Math.max(...validCumulativeValues) : 5;
    const maxHourly = validHourlyValues.length > 0 ? Math.max(...validHourlyValues) : 2;

    console.log('🔍 Debug scale pluviometri:', { maxCumulative, maxHourly });

    // Calcola le scale con margine del 20% e arrotondamento intelligente
    let cumulativeMax = Math.ceil(maxCumulative * 1.2);
    let hourlyMax = Math.ceil(maxHourly * 1.2);

    // Arrotonda a valori sensati per la scala cumulativa
    if (cumulativeMax <= 5) cumulativeMax = 5;
    else if (cumulativeMax <= 10) cumulativeMax = 10;
    else if (cumulativeMax <= 15) cumulativeMax = 15;
    else if (cumulativeMax <= 20) cumulativeMax = 20;
    else if (cumulativeMax <= 30) cumulativeMax = 30;
    else cumulativeMax = Math.ceil(cumulativeMax / 10) * 10;

    // Arrotonda a valori sensati per la scala oraria
    if (hourlyMax <= 2) hourlyMax = 2;
    else if (hourlyMax <= 5) hourlyMax = 5;
    else if (hourlyMax <= 10) hourlyMax = 10;
    else hourlyMax = Math.ceil(hourlyMax / 5) * 5;

    // Calcola gli intervalli
    const cumulativeInterval = cumulativeMax <= 10 ? 2 : (cumulativeMax <= 20 ? 5 : 10);
    const hourlyInterval = hourlyMax <= 5 ? 1 : 2;

    console.log('🔍 Scale calcolate:', { cumulativeMax, hourlyMax, cumulativeInterval, hourlyInterval });

    yAxisConfig = [
      {
        type: 'value',
        name: '(mm)',
        nameLocation: 'end',
        nameGap: 10,
        position: 'left',
        min: 0,
        max: hourlyMax,
        interval: hourlyInterval,
        axisLabel: {
          color: '#333',
          fontSize: 12,
          formatter: (value: number) => `${value}`
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#f0f0f0',
            type: 'solid',
            width: 1
          }
        }
      },
      {
        type: 'value',
        name: '(mm)',
        nameLocation: 'end',
        nameGap: 10,
        position: 'right',
        min: 0,
        max: cumulativeMax,
        interval: cumulativeInterval,
        axisLabel: {
          color: 'rgb(114, 63, 148)',
          fontSize: 12,
          formatter: (value: number) => `${value}`
        },
        splitLine: {
          show: false
        }
      }
    ];
  }

  // 🚀 CONFIGURAZIONE AVANZATA BAROMETRI
  if (this.sensorMeta.key === 'barometri') {
    const validValues = values.filter(v => v !== null && v !== undefined);

    if (validValues.length > 0) {
      const minValue = Math.min(...validValues);
      const maxValue = Math.max(...validValues);
      const range = maxValue - minValue;

      if (range < 20) {
        const center = (minValue + maxValue) / 2;
        yAxisConfig.min = Math.floor(center - 15);
        yAxisConfig.max = Math.ceil(center + 15);
        yAxisConfig.interval = 2;
      }
      else if (range < 50) {
        const padding = range * 0.2;
        yAxisConfig.min = Math.floor(minValue - padding);
        yAxisConfig.max = Math.ceil(maxValue + padding);
        yAxisConfig.interval = 5;
      }
      else {
        yAxisConfig.min = Math.max(880, Math.floor((minValue - 10) / 10) * 10);
        yAxisConfig.max = Math.min(1050, Math.ceil((maxValue + 10) / 10) * 10);
        yAxisConfig.interval = 10;
      }
    } else {
      yAxisConfig.min = 980;
      yAxisConfig.max = 1030;
      yAxisConfig.interval = 5;
    }

    const currentRange = yAxisConfig.max - yAxisConfig.min;
    const divisions = currentRange / yAxisConfig.interval;
    if (divisions > 15) {
      yAxisConfig.interval = Math.ceil(currentRange / 10);
    } else if (divisions < 4) {
      yAxisConfig.interval = Math.max(1, Math.floor(currentRange / 6));
    }
  }

  // (lastHourlyIndex già calcolato sopra)

  return {
    grid: {
      left: '1%',
      right: '1%',
      top: '15%',
      bottom: '10%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: timestamps,
      // ✅ Mantieni sempre la griglia completa fino alle 24:00
      min: 0,
      max: timestamps.length - 1,
      axisTick: {
        alignWithLabel: true,
        inside: false,
        length: 4
      },
      axisLabel: {
        formatter: (value: string, index: number) => {
          if (!value || typeof value !== 'string') return '';

          const parts = value.split(' ');
          // Nascondi completamente lo slot GAP
          if (parts.length === 1 && value.endsWith('|gap')) return '';
          if (parts.length !== 2) return '';

          const [, time] = parts;
          if (!time || time.length < 2) return '';

          const hour = time.substring(0, 2);
          const minute = time.substring(3, 5);
          const isInValidRange = index >= 0 && index < timestamps.length;

          // Mostra solo 06, 12, 18 (niente 00:00 né 24:00)
          if (isInValidRange && ['06', '12', '18'].includes(hour)) {
            return time.substring(0, 5);
          }

          return '';
        },
        fontSize: 12,
        color: '#333',
        interval: 0
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: '#333'
        }
      }
    },
    yAxis: yAxisConfig,
    series,
    tooltip: tooltipFormatter,
    legend: {
      show: true,
      orient: "horizontal",
      x: "right",
      padding: 10,
      selectedMode: false,
      data: this.sensorMeta.key === 'pluviometri' 
        ? ['Istogramma cumulata oraria', 'precip. cumulata giornaliera']
        : [title]
    }
  };
}

// Ritorna l'ultimo indice che ha almeno un valore (orario o cumulata) e non è uno slot GAP
private getLastDataIndex(
  timestamps: string[],
  values: (number | null)[],
  cumulatedValues: (number | null)[]
): number {
  // Per i pluviometri, fermati all'ultima ora con valore orario (bar),
  // ignorando lo slot artificiale "24:00:00" e gli slot GAP.
  if (this.sensorMeta.key === 'pluviometri') {
    for (let i = timestamps.length - 1; i >= 0; i--) {
      const t = timestamps[i];
      if (!t || typeof t !== 'string') continue;
      if (t.endsWith('|gap')) continue;
      const time = t.split(' ')[1] || '';
      if (time.startsWith('24:')) continue; // non estendere fino a 24:00
      const hasHourly = values && typeof values[i] === 'number';
      if (hasHourly) return i;
    }
  }

  // Default: ultimo indice con almeno un dato (orario o cumulato)
  for (let i = timestamps.length - 1; i >= 0; i--) {
    const t = timestamps[i];
    if (!t || typeof t !== 'string') continue;
    if (t.endsWith('|gap')) continue;
    const hasHourly = values && typeof values[i] === 'number';
    const hasCum = cumulatedValues && typeof cumulatedValues[i] === 'number';
    if (hasHourly || hasCum) return i;
  }
  return Math.max(0, timestamps.length - 1);
}

  get sensorMeta() {
    return this.sensorMetaById[this.tipoSensoreId] || { key: '', title: 'Dati Sensore', unit: '' };
  }

  
}